import React, { useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, View, type LayoutChangeEvent } from 'react-native';
import type { Paginated } from '@hrms/shared';
import { Button } from './Button';
import { Icon } from './Icon';
import { EmptyState, Loading } from './States';
import { Text } from './Text';
import { useTheme } from './theme';

export interface Column<T> {
  key: string;
  title: string;
  /** cell renderer; defaults to String(row[key]) */
  render?: (row: T) => React.ReactNode;
  /** minimum width in px (default 120). Columns grow to fill wide screens. */
  width?: number;
  flex?: number;
  align?: 'left' | 'right' | 'center';
  /** sortable by default when the column shows a raw value or has sortValue */
  sortable?: boolean;
  sortValue?: (row: T) => string | number | null | undefined;
}

export interface DataTableProps<T> {
  rows: T[] | Paginated<T> | undefined;
  columns: Column<T>[];
  loading?: boolean;
  /** server-side pagination (when rows is a Paginated<T>) */
  onPageChange?: (page: number) => void;
  onRowPress?: (row: T) => void;
  keyExtractor?: (row: T, index: number) => string | number;
  emptyTitle?: string;
  emptyDescription?: string;
  testID?: string;
}

const isPaginated = <T,>(r: T[] | Paginated<T> | undefined): r is Paginated<T> => !!r && !Array.isArray(r) && Array.isArray((r as Paginated<T>).data);
const raw = (row: unknown, key: string) => (row as Record<string, unknown>)[key];

/**
 * Table with sortable headers, horizontal scroll on narrow screens, pagination for
 * Laravel paginators, and optional row press.
 */
export function DataTable<T>({ rows, columns, loading, onPageChange, onRowPress, keyExtractor, emptyTitle = 'Nothing here yet', emptyDescription, testID }: DataTableProps<T>) {
  const { colors } = useTheme();
  const [w, setW] = useState(0);
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const data = useMemo(() => (isPaginated(rows) ? rows.data : rows ?? []), [rows]);
  const sorted = useMemo(() => {
    if (!sort) return data;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return data;
    const val = (r: T) => (col.sortValue ? col.sortValue(r) : raw(r, col.key)) as string | number | null | undefined;
    return [...data].sort((a, b) => {
      const x = val(a);
      const y = val(b);
      if (x == null) return 1;
      if (y == null) return -1;
      return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true })) * sort.dir;
    });
  }, [data, sort, columns]);

  const minTotal = columns.reduce((a, c) => a + (c.width ?? 120), 0);
  const tableW = Math.max(w, minTotal);
  const cell = (c: Column<T>) => ({ flexGrow: c.flex ?? 1, flexBasis: c.width ?? 120, flexShrink: 0, paddingHorizontal: 12, justifyContent: 'center' as const, alignItems: (c.align === 'right' ? 'flex-end' : c.align === 'center' ? 'center' : 'flex-start') as 'flex-end' | 'center' | 'flex-start' });

  const pager = isPaginated(rows) && rows.last_page > 1 && onPageChange;

  return (
    <View testID={testID} onLayout={(e: LayoutChangeEvent) => setW(e.nativeEvent.layout.width)} style={{ backgroundColor: colors.surface, borderRadius: 12, borderWidth: 1, borderColor: colors.border, overflow: 'hidden' }}>
      <ScrollView horizontal showsHorizontalScrollIndicator bounces={false}>
        <View style={{ width: tableW || undefined, minWidth: '100%' }}>
          <View accessibilityRole="header" style={{ flexDirection: 'row', backgroundColor: colors.surface2, borderBottomWidth: 1, borderBottomColor: colors.border, minHeight: 38 }}>
            {columns.map((c) => {
              const canSort = c.sortable ?? (!c.render || !!c.sortValue);
              const active = sort?.key === c.key;
              return (
                <Pressable
                  key={c.key}
                  disabled={!canSort}
                  accessibilityRole={canSort ? 'button' : undefined}
                  accessibilityLabel={canSort ? `Sort by ${c.title}` : c.title}
                  onPress={() => setSort(active ? (sort!.dir === 1 ? { key: c.key, dir: -1 } : null) : { key: c.key, dir: 1 })}
                  style={[cell(c), { flexDirection: 'row', alignItems: 'center', gap: 4, justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start' }]}
                >
                  <Text variant="caption" color={active ? colors.primary : colors.muted}>
                    {c.title}
                  </Text>
                  {active && <Icon name={sort!.dir === 1 ? 'chevronUp' : 'chevronDown'} size={12} color={colors.primary} />}
                </Pressable>
              );
            })}
          </View>
          {loading && !data.length ? (
            <Loading />
          ) : !sorted.length ? (
            <View style={{ width: w || undefined }}>
              <EmptyState title={emptyTitle} description={emptyDescription} />
            </View>
          ) : (
            sorted.map((row, i) => (
              <Pressable
                key={keyExtractor ? keyExtractor(row, i) : ((raw(row, 'id') as string | number | undefined) ?? i)}
                disabled={!onRowPress}
                onPress={() => onRowPress?.(row)}
                // On web a 'button' role renders <button>, and rows often hold their own action buttons
                // (nested <button> is invalid HTML), so web rows stay plain clickable, focusable elements.
                accessibilityRole={onRowPress && Platform.OS !== 'web' ? 'button' : undefined}
                focusable={!!onRowPress}
                style={({ pressed }) => ({ flexDirection: 'row', minHeight: 44, paddingVertical: 8, borderBottomWidth: i < sorted.length - 1 ? 1 : 0, borderBottomColor: colors.border, backgroundColor: pressed ? colors.hover : 'transparent' })}
              >
                {columns.map((c) => {
                  const content = c.render ? c.render(row) : raw(row, c.key);
                  return (
                    <View key={c.key} style={cell(c)}>
                      {typeof content === 'string' || typeof content === 'number' || content == null ? (
                        <Text numberOfLines={2} align={c.align}>
                          {content == null || content === '' ? '—' : String(content)}
                        </Text>
                      ) : (
                        (content as React.ReactNode)
                      )}
                    </View>
                  );
                })}
              </Pressable>
            ))
          )}
        </View>
      </ScrollView>
      {pager && isPaginated(rows) && (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 10, borderTopWidth: 1, borderTopColor: colors.border, gap: 8, flexWrap: 'wrap' }}>
          <Text variant="small">
            {rows.total} records · page {rows.current_page} of {rows.last_page}
          </Text>
          <View style={{ flexDirection: 'row', gap: 6 }}>
            <Button size="sm" icon="chevronLeft" title="Prev" disabled={rows.current_page <= 1} onPress={() => onPageChange!(rows.current_page - 1)} />
            <Button size="sm" iconRight="chevronRight" title="Next" disabled={rows.current_page >= rows.last_page} onPress={() => onPageChange!(rows.current_page + 1)} />
          </View>
        </View>
      )}
    </View>
  );
}
